import React, { type ComponentType } from 'react';

import { BitbucketIcon } from '@atlaskit/logo/bitbucket-icon';
import { CompassIcon } from '@atlaskit/logo/compass/icon';
import { ConfluenceIcon } from '@atlaskit/logo/confluence-icon';
import { GoalsIcon } from '@atlaskit/logo/goals/icon';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import { TrelloIcon } from '@atlaskit/logo/trello-icon';

import { formatProduct } from './formatProduct.ts';
import type { EnhancedSmartLink, SmartLinkIcon, SmartLinkLogoProps } from './smartLinkTypes.ts';
import { groupsUnderGoals } from './townsquare.ts';

type ProductLogo = ComponentType<SmartLinkLogoProps>;

type ProductAppIconSource = Pick<EnhancedSmartLink, 'product'>;

const FIRST_PARTY_LOGOS: Record<string, ProductLogo> = {
	bitbucket: BitbucketIcon,
	compass: CompassIcon,
	confluence: ConfluenceIcon,
	jira: JiraIcon,
	loom: LoomIcon,
	trello: TrelloIcon,
};

const getAppIconId = (product: string | undefined): string | undefined => {
	if (groupsUnderGoals(product)) {
		return 'goals';
	}

	const key = product?.toLowerCase().trim();
	if (!key) {
		return undefined;
	}

	if (key === 'jira' || key.startsWith('jira ') || key.startsWith('jira-')) {
		return 'jira';
	}

	return FIRST_PARTY_LOGOS[key] ? key : undefined;
};

// Hub chips (byline stack, panel section expand) show the first-party app icon, not the
// content-type glyph. Menu and panel rows still use extractSmartLinkIcon / getSmartLinkIcon.
export const getProductAppIcon = (source: ProductAppIconSource): SmartLinkIcon | undefined => {
	const appIconId = getAppIconId(source.product);
	if (!appIconId) {
		return undefined;
	}

	const Logo = appIconId === 'goals' ? GoalsIcon : FIRST_PARTY_LOGOS[appIconId];
	if (!Logo) {
		return undefined;
	}

	return {
		node: React.createElement(Logo, {
			appearance: 'brand',
			size: 'xxsmall',
			testId: `${appIconId}-app-icon`,
		}),
		key: `product:${appIconId}`,
		label: formatProduct(source.product) ?? source.product,
		kind: 'logo',
	};
};
